import { queryOptions } from "@tanstack/react-query";
import { getAllEnginePages, getEngineApi } from "@/lib/api";
import type { SessionData } from "@/lib/auth-client";
import { HIDDEN_TOOLKITS } from "@/lib/const";
import type { OrgProjectScope } from "@/lib/resource-context";
import type { CatalogEntry } from "../types";
import { toolCatalogKeys } from "./query-keys";

// The engine identifies the requesting user by email. The route prefetch and
// the page both derive `userId` through this helper — if they computed it
// independently and drifted, their cache keys would diverge and the catalog
// would silently fetch twice.
export const catalogUserId = (session: SessionData): string | undefined =>
  session?.user?.rawEmail ?? session?.user?.email ?? undefined;

// The catalog changes rarely; avoid refetching it on every navigation back.
const STALE_TIME_MS = 5 * 60 * 1000;

// `userId` (the session user's email) makes the engine evaluate per-toolkit
// requirement readiness (`met`); without it, requirements are listed but not
// resolved. It is part of the cache key so switching users doesn't cross wires.
export const toolCatalogQueryOptions = (
  scope: OrgProjectScope,
  userId?: string
) => {
  const client = getEngineApi(scope);

  return queryOptions<CatalogEntry[]>({
    queryKey: userId
      ? [...toolCatalogKeys.list({ scope }), "user", userId]
      : toolCatalogKeys.list({ scope }),
    queryFn: async () => {
      const data = await getAllEnginePages<CatalogEntry>(
        client,
        "/tool_catalog",
        {
          ...(userId ? { user_id: userId } : {}),
        }
      );
      return data.items.filter(
        (entry) => !HIDDEN_TOOLKITS.has(entry.name.toLowerCase())
      );
    },
    staleTime: STALE_TIME_MS,
  });
};
